// Stub for expo-router on web platform
// Web builds use react-router-dom, not expo-router
// This bridges expo-router APIs to react-router-dom equivalents
// preventing "Failed to resolve module specifier 'expo-router'" errors

import {
  useLocation,
  useParams,
  resolvePath,
  UNSAFE_NavigationContext as RouterNavigationContext,
  UNSAFE_LocationContext as RouterLocationContext,
} from 'react-router-dom';
import { useContext, useEffect } from 'react';

// Returned by useRouter() when the caller renders OUTSIDE a <Router>. This is
// not a defensive nicety: on the legacy web app UnifiedAuth0Provider calls
// useRouter() and App.tsx mounts it ABOVE <Router>, so this path runs on every
// page load.
const NOOP_ROUTER = {
  push: () => {},
  replace: () => {},
  back: () => {},
  canGoBack: () => false,
  setParams: () => {},
};

/**
 * Bridge expo-router's useRouter to react-router-dom's useNavigate.
 * Provides push(), replace(), back() matching the expo-router API.
 */
export function useRouter() {
  // 2026-09-17 (hooks debt): this used to be `try { navigate = useNavigate() }
  // catch { return no-ops }`. A hook inside a try block is a Rules-of-Hooks
  // violation, and useNavigate() is the hook that THROWS outside a Router. The
  // two contexts below are what useNavigate() reads internally; read directly
  // they are simply `null` outside a Router, so both hooks run unconditionally
  // on every render and nothing can throw. Pinned by
  // src/stubs/__tests__/expoRouterWebStub.test.tsx (inside AND outside).
  const navigationContext = useContext(RouterNavigationContext);
  const locationContext = useContext(RouterLocationContext);

  if (!navigationContext || !locationContext) {
    // Component not inside a Router context - return safe no-ops
    return NOOP_ROUTER;
  }

  const { navigator, basename } = navigationContext;
  const go = (path, replace) => {
    const to = typeof path === 'string' ? path : '/';
    // The same resolution useNavigate() performs. Every caller passes an
    // absolute path; resolvePath also handles a relative one against the
    // current URL, which is expo-router's own (URL-relative) behaviour.
    const resolved = resolvePath(to, locationContext.location.pathname);
    if (basename && basename !== '/') {
      resolved.pathname =
        resolved.pathname === '/' ? basename : `${basename.replace(/\/+$/, '')}${resolved.pathname}`;
    }
    (replace ? navigator.replace : navigator.push)(resolved, undefined, { replace });
  };

  return {
    push: (path) => {
      try {
        go(path, false);
      } catch (e) {
        console.warn('[expo-router stub] push failed:', e);
      }
    },
    replace: (path) => {
      try {
        go(path, true);
      } catch (e) {
        console.warn('[expo-router stub] replace failed:', e);
      }
    },
    back: () => {
      try {
        navigator.go(-1);
      } catch (e) {
        console.warn('[expo-router stub] back failed:', e);
      }
    },
    canGoBack: () => true,
    setParams: () => {},
  };
}

/**
 * Bridge expo-router's usePathname to react-router-dom's useLocation().pathname.
 */
export function usePathname() {
  try {
    const location = useLocation();
    return location.pathname;
  } catch (e) {
    return '/';
  }
}

/**
 * Bridge expo-router's useLocalSearchParams to react-router-dom's
 * useParams() (path params) + useSearchParams() (query params).
 */
export function useLocalSearchParams() {
  // 2026-09-17 (hooks debt): was useParams() + useSearchParams() inside a
  // try/catch. useParams() never throws (its context has a default value);
  // useSearchParams() does, outside a Router. The location context is what it
  // reads, and it is `null` outside a Router — so both hooks run
  // unconditionally and the outside-a-Router answer is still `{}`.
  const params = useParams();
  const locationContext = useContext(RouterLocationContext);
  if (!locationContext) {
    return {};
  }
  const queryParams = Object.fromEntries(
    new URLSearchParams(locationContext.location.search).entries(),
  );
  return { ...params, ...queryParams };
}

/**
 * Bridge expo-router's useGlobalSearchParams (same as local on web).
 */
export function useGlobalSearchParams() {
  return useLocalSearchParams();
}

/**
 * Bridge expo-router's useSegments to pathname segments.
 */
export function useSegments() {
  try {
    const location = useLocation();
    return location.pathname.split('/').filter(Boolean);
  } catch (e) {
    return [];
  }
}

/**
 * Bridge expo-router's useFocusEffect to React's useEffect.
 * On web, screens are always "focused" (no tab navigation hiding),
 * so useEffect with empty deps is equivalent.
 */
export function useFocusEffect(callback) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const cleanup = callback();
    return typeof cleanup === 'function' ? cleanup : undefined;
  }, []);
}

/**
 * Stack layout component - no-op on web (web uses react-router-dom Routes).
 */
export function Stack({ children }) {
  return children || null;
}
Stack.Screen = function StackScreen() {
  return null;
};

/**
 * Tabs layout component - no-op on web (web uses its own tab navigation).
 */
export function Tabs({ children }) {
  return children || null;
}
Tabs.Screen = function TabScreen() {
  return null;
};

/**
 * Link component - re-exported from react-router-dom.
 */
export { Link } from 'react-router-dom';

/**
 * Redirect component - bridge to react-router-dom Navigate.
 */
export function Redirect({ href }) {
  const router = useRouter();
  useEffect(() => {
    if (href) {
      router.replace(href);
    }
  }, [href, router]);
  return null;
}

export default {};
